"use client"

import { useEffect, useState, useRef } from "react"
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Info,
  CuboidIcon as Cube,
  Building,
  BuildingIcon as Buildings,
} from "lucide-react"
import BuilderService from "../../../../services/BuilderService"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import { useUpdateUserDetails, useUserDetails } from "../../../../../redux/hooks/UserDetails"
import Loader from "../../../../Loader/Loader"
import BillingService from "../../../../services/BillingService";

// Define interfaces for the API response
interface Feature {
  _id: string
  name: string
  heading: string
  description: string
  media_link: string
  media_type: string
  active: boolean
  seq: number
  limit: number
  parent_feature: string | null
  sub_features: any[]
  createdAt: string
  updatedAt: string
}

interface Plan {
  _id: string
  planName: string
  totalAppsCount: number
  totalLeadsCount: number
  dailyChatLimit: number
  monthlyChatLimit: number
  display_Plan_name?: string
  price?: number
  monthlyPlan: boolean
  chargeBeePlanId: string
  features: Feature[]
  createdAt?: string
  updatedAt: string
  customDomainLimit: boolean
  inLineEditorLimit: boolean
}

const builderService = new BuilderService()

const PurchasingPLan = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [plans, setPlans] = useState<Plan[]>([])
  const [filteredPlans, setFilteredPlans] = useState<Plan[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isMonthly, setIsMonthly] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false);
  const [isChargebeeReady, setIsChargebeeReady] = useState<boolean>(false);
  const [cbInstance, setCbInstance] = useState<any>(null);
  const [updatingPlanId, setUpdatingPlanId] = useState<string | null>(null);
  const billingService = new BillingService();
  const carouselRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { updateUserDetail } = useUpdateUserDetails()
  const { userDetail } = useUserDetails()

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await builderService.getAllPlans()
        if (response.statusCode === 200 && response.data) {
          setPlans(response.data)
          // Initially filter for monthly plans
          setFilteredPlans(response.data.filter((plan:any) => plan.monthlyPlan === true))
        }
      } catch (error) {
        console.error("Error fetching plans:", error)
        toast.error("Failed to load plans")
      } finally {
        setIsLoading(false)
      }
    }

    fetchPlans()
  }, [])

  // Filter plans when toggle changes
  useEffect(() => {
    if (plans.length > 0) {
      setFilteredPlans(plans.filter((plan) => plan.monthlyPlan === isMonthly))
      setCurrentIndex(0)
      // Reset scroll position when changing plan type
      if (carouselRef.current) {
        carouselRef.current.scrollLeft = 0
      }
    }
  }, [isMonthly, plans])

  useEffect(() => {
    const initChargebee = async () => {
      try {
        if (!window.Chargebee) {
          const script = document.createElement('script');
          script.src = 'https://js.chargebee.com/v2/chargebee.js';
          script.async = true;
          
          // Wait for script to load
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }
  
        // Initialize Chargebee
        window.Chargebee.init({
          site: process.env.REACT_APP_CHARGEBEE_SITE_URL || '',
          publishableKey: process.env.REACT_APP_CHARGEBEE_API_KEY || ''
        });
  
        // Get instance and store it
        const instance = window.Chargebee.getInstance();
        setCbInstance(instance);
        setIsChargebeeReady(true);
      } catch (error) {
        console.log('Failed to initialize Chargebee:', error);
      }
    };
  
    initChargebee();
  
    // Cleanup
    return () => {
      setIsChargebeeReady(false);
      setCbInstance(null);
    };
  }, []);
  const getIconForPlan = (planName: string) => {
    const name = planName.toLowerCase()
    if (name.includes("free")) {
      return <Cube className="w-8 h-8 text-gray-700" />
    } else if (name.includes("pro")) {
      return <Building className="w-8 h-8 text-pink-500" />
    } else if (name.includes("business")) {
      return <Buildings className="w-8 h-8 text-blue-500" />
    }
    return <Cube className="w-8 h-8 text-gray-700" />
  }

  const scroll = (direction: "left" | "right") => {
    if (carouselRef.current) {
      const { scrollLeft, clientWidth } = carouselRef.current
      const scrollTo = direction === "left" ? scrollLeft - clientWidth / 1.5 : scrollLeft + clientWidth / 1.5

      carouselRef.current.scrollTo({
        left: scrollTo,
        behavior: "smooth",
      })
    }
  }

  const handleScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current
      const newIndex = Math.round(scrollLeft / (scrollWidth / filteredPlans.length))
      setCurrentIndex(newIndex)
    }
  }

  const formatPlanName = (name: string) => {
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const handlePlanSelection = async (plan: Plan) => {
    // Check if it's an Enterprise plan
    if (plan.display_Plan_name === "Enterprise") {
      toast.info(
        <div>
          <p className="font-medium">Enterprise Plan</p>
          <p className="text-sm">Please contact our customer care team for Enterprise plan details.</p>
        </div>,
        {
          autoClose: 5000,
          position: "top-center",
        }
      );
      return;
    }

    console.log("Selected plan:", plan)
    try {
      // First check if user is an organization user
      if (userDetail?.ogUser) {
        toast.error(
          <div>
            <p className="font-medium">Organization User</p>
            <p className="text-sm">Please contact your organization administrator to update your plan.</p>
          </div>,
          {
            autoClose: 5000,
            position: "top-center",
          },
        )
        return
      }

      setUpdatingPlanId(plan._id); 
      if (!isChargebeeReady || !cbInstance) {
        console.log('Payment system not ready. Please try again.');
        return;
      }
    
      try {
        setIsProcessing(true);
        const planToChange = plan.chargeBeePlanId;
        
        const currentPageUrl = window.location.href;
        const currentPlanName = userDetail?.ogSubscriptionId?.planName;
        const subscriptionId = userDetail?.subscriptionId?._id || null;
        const userId = userDetail?._id;
        const response = await billingService.updateSubscription(planToChange, currentPageUrl, currentPlanName, subscriptionId, userId);
    
        if (response.success && response.hosted_url_data?.hosted_page) {
          const hostedPage = response.hosted_url_data.hosted_page;
          
          cbInstance.openCheckout({
            hostedPage: () => Promise.resolve(hostedPage),
            success: (hostedPageId: string) => {
              toast.success('Subscription updated successfully!');
              setTimeout(() => window.location.reload(), 2000);
            },
            error: (err: any) => {
              console.error('Checkout error:', err);
              toast.error('Failed to process payment');
              setIsProcessing(false);
            setUpdatingPlanId(null);
            },
            close: () => {
              setIsProcessing(false);
              setUpdatingPlanId(null);
              console.log('Checkout closed');
            },
            step: (step: any) => {
              console.log('Checkout step:', step);
              if (step === 'payment_success') {
                // Show success message in a modal or toast
                toast.success(
                  <div className="flex flex-col items-center">
                    <h3 className="text-lg font-bold mb-2">Payment Successful!</h3>
                    <p className="text-sm">Thank you for upgrading your plan.</p>
                  </div>,
                  {
                    autoClose: 3000,
                    position: "top-center"
                  }
                );
                
                // Close checkout after showing success message
                setTimeout(() => {
                  if (cbInstance) {
                    cbInstance.closeAll();
                    // Reload page after closing checkout
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  }
                }, 2000);
              }            },
            embed: true, // Keep it embedded
            layout: {
              present: 'popup', // Show as popup modal
              close_button: true // Allow closing
            }          });
        } else {
          throw new Error('Invalid hosted page data');
        }
      } catch (error) {
        toast.error('Failed to update subscription');
        console.error('Error:', error);
        setIsProcessing(false);
        setUpdatingPlanId(null);
      } 
      // const response = await builderService.updateUserPlanSubscription(plan._id)

      // if (response.success && response.data?.user) {
      //   // Update user details in Redux store
      //   updateUserDetail(response.data.user)

      //   // Show success message
      //   toast.success("Plan updated successfully!")

      //   // Navigate back or to dashboard
      //   navigate("/planinfo")
      // } 
      // else {
      //   // Handle specific error cases
      //   if (response.message?.includes("organization user")) {
      //     toast.error(
      //       <div>
      //         <p className="font-medium">Organization User</p>
      //         <p className="text-sm">Please contact your organization administrator to update your plan.</p>
      //       </div>,
      //       {
      //         autoClose: 5000,
      //         position: "top-center",
      //       },
      //     )
      //   } else {
      //     toast.error(response.message || "Failed to update plan")
      //   }
      // }
    } catch (error: any) {
      console.error("Error updating plan:", error)
      // Check if the error is from the organization user case
      if (error.response?.data?.message?.includes("organization user")) {
        toast.error(
          <div>
            <p className="font-medium">Organization User</p>
            <p className="text-sm">Please contact your organization administrator to update your plan.</p>
          </div>,
          {
            autoClose: 5000,
            position: "top-center",
          },
        )
      } else {
        toast.error(error.response?.data?.message || "Failed to update plan")
      }
    } finally {
      setUpdatingPlanId(null); // Make sure to clear the ID even if there's an error
    }
  }

  if (isLoading) {
    return (
      <Loader
        isLoading={isLoading}
        imageUrl="https://dlvkyia8i4zmz.cloudfront.net/JllcrYgcTSeRguZ1fEhe_advance_t_b__2___3_.svg"
        text=""
      />
    )
  }

  const showLeftArrow = currentIndex > 0
  const showRightArrow = filteredPlans.length > 3 && currentIndex < filteredPlans.length - 3

  return (
    <div style={{ width: '1124px' }} className="container mx-auto p-[10px] rounded-[20px] plans-parentcard">
      {/* Monthly/Yearly Toggle */}
      <div className="flex justify-center mb-8">
        <div className="sp-border p-[4px] rounded-[12px]">
          <div className="flex justify-between">
            <button
              className={`rounded-[12px] px-[22px] py-[8px] text-sm font-medium transition-colors ${
                isMonthly ? "text-white" : "transparent"
              }`}
              style={
                isMonthly
                  ? {
                      background: "linear-gradient(107.87deg, #FF3AB6 2.39%, #FB5F65 95.19%)",
                    }
                  : {}
              }
              onClick={() => setIsMonthly(true)}
            >
              <span className="font-inter">Monthly</span>
            </button>

            <button
              className={`rounded-[12px] px-[20px] py-[8px] text-sm font-medium transition-colors ${
                !isMonthly ? "text-white" : "transparent"
              }`}
              style={
                !isMonthly
                  ? {
                      background: "linear-gradient(107.87deg, #FF3AB6 2.39%, #FB5F65 95.19%)",
                    }
                  : {}
              }
              onClick={() => setIsMonthly(false)}
            >
              <span className="font-inter">Yearly</span>
              {!isMonthly && <span className="ml-1 text-xs text-green-400">Save 20%</span>}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-[10px] md:gap-[10px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {filteredPlans.map((plan) => (
          <div
            key={plan._id}
            className={`flex flex-col sp-border rounded-[12px] overflow-hidden ${
              plan.planName.toLowerCase().includes("pro") ? "shadow-lg border-pink-300" : ""
            }`}
          >
            {/* Header */}
            <div className="p-[28px] pricing-b-bottom">
              <h3 className="pricing-h font-sora">
                {plan.display_Plan_name || formatPlanName(plan.planName)}
              </h3>
            </div>

            {/* Price Section */}
            <div className="p-[28px] flex flex-col gap-[28px]">
              <div className="flex items-baseline">
                {plan.display_Plan_name === "Enterprise" ? (
                  <span className="pricing-n">Custom</span>
                  
                ) : (
                  <>
                    <span className="pricing-n">${plan.price}</span>
                    <span className="per-m ml-1">/{isMonthly ? 'month' : 'year'}</span>
                  </>
                )}
              </div>

            <button
              onClick={() => handlePlanSelection(plan)}
              disabled={updatingPlanId === plan._id}
              className={`
                w-full rounded-[12px] py-[12px] px-[24px] text-sm font-medium transition-all
                ${plan.planName.toLowerCase().includes("pro")
                  ? "bg-gradient-to-r from-[#FF3AB6] to-[#FB5F65] text-white hover:opacity-90"
                  : "border border-[#FF3AB6] text-[#FF3AB6] hover:bg-pink-50"
                }
                ${updatingPlanId === plan._id ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {updatingPlanId === plan._id ? (
                <div className="flex items-center justify-center">
                  <svg 
                    className="animate-spin -ml-1 mr-3 h-5 w-5" 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24"
                  >
                    <circle 
                      className="opacity-25" 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Processing...
                </div>
              ) : (
                <span className="font-medium">
                  {plan.display_Plan_name === "Enterprise" ? "Contact Sales" : "Select Plan"}
                </span>
              )}
            </button>
            </div>

            {/* Features List */}
            <div className="flex-1">
              <div className="pricing-b px-[10px] msg-count flex justify-center items-center h-[80px]">
                {plan.display_Plan_name === "Enterprise" ? (
                  "Custom"
                ) : (
                  `${plan.monthlyChatLimit} messages/month`
                )}
              </div>

              {plan.features.map((feature, index) => (
                <div
                  key={feature._id}
                  className="pricing-b px-[10px] flex justify-center items-center h-[80px]"
                >
                  <span>
                    <Check className="h-5 w-5 text-[#01A66F]" />
                  </span>
                  <span className="ml-2 text-sm">{feature.name.split("_").join(" ")}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PurchasingPLan
